<template>
  <v-card outlined>
    <v-card-title class="py-2">
      {{ use_language.product }}
      <v-chip class="ml-2" small>{{ resultCount }} {{ use_language.item_unit }}</v-chip>
      <span v-if="keyword" class="caption grey--text ml-2">{{ use_language.search }}: “{{ keyword }}”</span>
      <v-spacer />
      <v-btn icon small :loading="loading" @click="load">
        <v-icon small>mdi-refresh</v-icon>
      </v-btn>
    </v-card-title>
    <v-divider />

    <div v-if="!warehouseId" class="text-center grey--text py-12">
      {{ use_language.select_warehouse_first }}
    </div>

    <div v-else-if="loading" class="pa-3">
      <v-row dense>
        <v-col v-for="n in take" :key="'sk-' + n" cols="6" sm="4" md="3">
          <v-skeleton-loader type="image, list-item-two-line" />
        </v-col>
      </v-row>
    </div>

    <div v-else-if="!items.length" class="text-center grey--text py-12">
      {{ use_language.product_not_found }}{{ keyword ? ' “' + keyword + '”' : '' }}
    </div>

    <div v-else class="pa-3">
      <v-row dense>
        <v-col v-for="p in items" :key="p.stockShopID" cols="6" sm="4" md="3">
          <v-card
            outlined
            :hover="!isOut(p)"
            :ripple="!isOut(p)"
            class="prod"
            :class="{ 'prod--out': isOut(p) }"
            @click="pick(p)"
          >
            <div class="prod-img">
              <v-img v-if="p.photoLink" :src="p.photoLink" height="100%" contain />
              <div v-else class="d-flex align-center justify-center fill-height">
                <v-icon color="grey lighten-1">mdi-image-off</v-icon>
              </div>
              <div v-if="isOut(p)" class="prod-badge">{{ use_language.sold_out }}</div>
            </div>
            <div class="prod-body">
              <div class="prod-name" :title="p.name">{{ p.name }}</div>
              <div class="prod-sku">{{ p.sku || '-' }} / {{ p.barcode || '-' }}</div>
              <div class="d-flex align-center justify-space-between mt-1">
                <span class="prod-price">
                  {{ p.salePrice == null ? '-' : formatPrice(p.salePrice) }}
                </span>
                <span class="prod-stock" :class="{ 'red--text': isOut(p) }">
                  {{ use_language.remaining_short }} {{ p.quantityAvailable }}
                </span>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <div v-if="pageCount > 1" class="d-flex justify-center pt-3">
        <v-pagination v-model="page" :length="pageCount" :total-visible="7" />
      </div>
    </div>
  </v-card>
</template>

<script>
import axios from 'axios'
import { stockService_dotnet } from '@/website/global'
import { get_languages } from '@/website/global_function'

export default {
  name: 'PosProductGrid',
  props: {
    warehouseId: { type: [Number, String], default: null },
    keyword: { type: String, default: '' }
  },
  data: () => ({
    use_language: {},
    header_token: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + localStorage.getItem('authorization_token') },
    items: [],
    resultCount: 0,
    page: 1,
    take: 12,
    loading: false
  }),
  computed: {
    pageCount () {
      return Math.ceil(this.resultCount / this.take) || 1
    }
  },
  watch: {
    warehouseId: {
      immediate: true,
      handler () {
        this.page = 1
        this.load()
      }
    },
    keyword () {
      if (this.page !== 1) this.page = 1
      else this.load()
    },
    page () {
      this.load()
    }
  },
  async created () {
    this.use_language = await this.get_languages(localStorage.getItem("set_language"))
  },
  methods: {
    get_languages,
    async load () {
      if (!this.warehouseId) {
        this.items = []
        this.resultCount = 0
        return
      }
      this.loading = true
      try {
        const res = await axios.post(
          stockService_dotnet + 'Stock/get-stock-list-addorder',
          {
            shopID: localStorage.getItem('shop_id'),
            keyword: this.keyword || null,
            sku: null,
            name: null,
            barcode: null,
            warehouseID: this.warehouseId,
            isNeedResultCount: true,
            skipProductMasterItemIDList: [],
            isProductSet: null,
            skip: (this.page - 1) * this.take,
            take: this.take
          },
          { headers: this.header_token }
        )
        this.items = (res.data && res.data.stockList) || []
        this.resultCount = (res.data && res.data.resultCount) || 0
      } catch (err) {
        this.$swal({ position: 'top', type: 'error', text: err.message })
        this.items = []
        this.resultCount = 0
      }
      this.loading = false
    },
    isOut (p) {
      return Number(p.quantityAvailable || 0) <= 0
    },
    pick (p) {
      if (this.isOut(p)) {
        this.$swal({
          position: 'top',
          toast: true,
          timer: 1500,
          showConfirmButton: false,
          type: 'warning',
          title: this.use_language.product_out_of_stock + ': ' + p.name
        })
        return
      }
      this.$emit('add-item', p)
    },
    formatPrice (v) {
      return Number(v || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }
  }
}
</script>

<style scoped>
.prod {
  cursor: pointer;
  overflow: hidden;
}
.prod--out {
  cursor: not-allowed;
  opacity: 0.55;
}
.prod-img {
  position: relative;
  height: 110px;
  background: #fafafa;
  border-bottom: 1px solid #eceff1;
}
.prod-badge {
  position: absolute;
  top: 6px;
  left: 6px;
  background: #d50000;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  padding: 5px 10px;
  border-radius: 4px;
}
.prod-body {
  padding: 6px 8px 8px;
}
.prod-name {
  font-size: 12px;
  line-height: 1.25;
  height: 30px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.prod-sku {
  font-size: 10px;
  color: #90a4ae;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.prod-price {
  font-size: 13px;
  font-weight: 600;
}
.prod-stock {
  font-size: 10px;
  color: #78909c;
}
</style>
